/**
 * Device Info Bridge
 * 
 * Provides device and browser information to the Quapp.
 * Full support on web via Navigator API.
 */

import { BaseBridge } from '../BaseBridge'
import type { BridgeAvailability, BridgeCapabilities, DeviceInfo } from '../types'

export class DeviceInfoBridge extends BaseBridge {
  readonly bridgeName = 'DeviceInfo'
  readonly availability: BridgeAvailability = 'full'
  readonly methods = ['getDeviceInfo']
  
  async handleCall(method: string, _args: any[]): Promise<any> {
    switch (method) {
      case 'getDeviceInfo':
        return JSON.stringify(this.getDeviceInfo())
      default:
        throw this.createMethodNotFoundError(method)
    }
  }
  
  private getDeviceInfo(): DeviceInfo {
    return {
      platform: this.detectPlatform(),
      os: this.detectOS(),
      osVersion: this.detectOSVersion(),
      browser: this.detectBrowser(),
      browserVersion: this.detectBrowserVersion(),
      screenWidthPx: window.screen.width,
      screenHeightPx: window.screen.height,
      screenDensity: window.devicePixelRatio || 1,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      language: navigator.language,
      languages: navigator.languages,
      isOnline: navigator.onLine,
      deviceMemory: (navigator as any).deviceMemory || null,
      hardwareConcurrency: navigator.hardwareConcurrency || null,
      touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      maxTouchPoints: navigator.maxTouchPoints || 0,
      quappEnvironment: 'web-frame',
      quappVersion: '1.0.0'
    }
  }
  
  private detectPlatform(): DeviceInfo['platform'] {
    const ua = navigator.userAgent.toLowerCase()
    
    if (/iphone|ipad|ipod/.test(ua)) return 'ios'
    if (/android/.test(ua)) return 'android'
    if (/windows/.test(ua)) return 'windows'
    if (/macintosh|mac os x/.test(ua)) return 'macos'
    if (/linux/.test(ua)) return 'linux'
    
    return 'web'
  }
  
  private detectOS(): string {
    const ua = navigator.userAgent
    
    if (/Windows/.test(ua)) return 'Windows'
    if (/Mac OS X/.test(ua)) return 'macOS'
    if (/Android/.test(ua)) return 'Android'
    if (/iPhone|iPad|iPod/.test(ua)) return 'iOS'
    if (/Linux/.test(ua)) return 'Linux'
    
    return 'Unknown'
  }
  
  private detectOSVersion(): string {
    const ua = navigator.userAgent
    
    // Windows
    const winMatch = ua.match(/Windows NT (\d+\.\d+)/)
    if (winMatch) {
      const version = winMatch[1]
      const versions: Record<string, string> = {
        '10.0': '10/11',
        '6.3': '8.1',
        '6.2': '8',
        '6.1': '7'
      }
      return versions[version] || version
    }
    
    // macOS
    const macMatch = ua.match(/Mac OS X (\d+[._]\d+[._]?\d*)/)
    if (macMatch) return macMatch[1].replace(/_/g, '.')
    
    // iOS
    const iosMatch = ua.match(/OS (\d+[._]\d+[._]?\d*)/)
    if (iosMatch) return iosMatch[1].replace(/_/g, '.')
    
    // Android
    const androidMatch = ua.match(/Android (\d+\.?\d*\.?\d*)/)
    if (androidMatch) return androidMatch[1]
    
    return 'Unknown'
  }
  
  private detectBrowser(): string {
    const ua = navigator.userAgent
    
    if (/Edg\//.test(ua)) return 'Edge'
    if (/OPR\/|Opera/.test(ua)) return 'Opera'
    if (/Chrome\//.test(ua)) return 'Chrome'
    if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return 'Safari'
    if (/Firefox\//.test(ua)) return 'Firefox'
    
    return 'Unknown'
  }
  
  private detectBrowserVersion(): string {
    const ua = navigator.userAgent
    
    // Edge
    const edgeMatch = ua.match(/Edg\/(\d+\.?\d*\.?\d*)/)
    if (edgeMatch) return edgeMatch[1]
    
    // Chrome
    const chromeMatch = ua.match(/Chrome\/(\d+\.?\d*\.?\d*)/)
    if (chromeMatch) return chromeMatch[1]
    
    // Firefox
    const firefoxMatch = ua.match(/Firefox\/(\d+\.?\d*\.?\d*)/)
    if (firefoxMatch) return firefoxMatch[1]
    
    // Safari
    const safariMatch = ua.match(/Version\/(\d+\.?\d*\.?\d*)/)
    if (safariMatch) return safariMatch[1]
    
    return 'Unknown'
  }
  
  getCapabilities(): BridgeCapabilities {
    return {
      getDeviceInfo: { available: true }
    }
  }
}

